/** Kỳ báo cáo: năm / quý / tháng → from_date, to_date */

export function pad2(n) {
  return String(n).padStart(2, "0");
}

export function ymd(y, m, d) {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

export function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

export function currentPeriodDefaults() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const quarter = Math.ceil(month / 3);
  return {
    period_type: "month",
    year,
    quarter,
    month,
    ...rangeFromPeriod("month", year, quarter, month),
    branch_id: "",
    payer_type: "",
    direction: "",
    group: "",
  };
}

export function rangeFromPeriod(periodType, year, quarter, month) {
  const y = Number(year) || new Date().getFullYear();
  if (periodType === "year") {
    return { from_date: ymd(y, 1, 1), to_date: ymd(y, 12, 31) };
  }
  if (periodType === "quarter") {
    const q = Math.min(4, Math.max(1, Number(quarter) || 1));
    const startMonth = (q - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    return {
      from_date: ymd(y, startMonth, 1),
      to_date: ymd(y, endMonth, daysInMonth(y, endMonth)),
    };
  }
  const m = Math.min(12, Math.max(1, Number(month) || 1));
  return {
    from_date: ymd(y, m, 1),
    to_date: ymd(y, m, daysInMonth(y, m)),
  };
}

export function periodLabel(filters) {
  const type = filters?.period_type || "month";
  const y = filters?.year;
  if (type === "year") return `Năm ${y}`;
  if (type === "quarter") return `Quý ${filters?.quarter}/${y}`;
  return `Tháng ${pad2(filters?.month)}/${y}`;
}

export function yearOptions(span = 6) {
  const current = new Date().getFullYear();
  return Array.from({ length: span }, (_, i) => current - i);
}

export function applyPeriodChange(prev, patch) {
  const next = { ...prev, ...patch };
  const range = rangeFromPeriod(
    next.period_type,
    next.year,
    next.quarter,
    next.month
  );
  return { ...next, ...range };
}

/** Gộp ledger theo ngày cho chart tháng */
export function buildDailyCashflowSeries(ledger = []) {
  const map = new Map();
  for (const row of ledger) {
    const key = String(row.entry_date || "").slice(0, 10);
    if (!key) continue;
    if (!map.has(key)) map.set(key, { date: key, total_in: 0, total_out: 0, net: 0 });
    const item = map.get(key);
    item.total_in += Number(row.amount_in || 0);
    item.total_out += Number(row.amount_out || 0);
    item.net = item.total_in - item.total_out;
  }
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/** Aging buckets cho công nợ */
export function buildAgingBuckets(unpaid = []) {
  const buckets = [
    { key: "0_7", label: "0–7 ngày", min: 0, max: 7, amount: 0, count: 0 },
    { key: "8_14", label: "8–14 ngày", min: 8, max: 14, amount: 0, count: 0 },
    { key: "15_29", label: "15–29 ngày", min: 15, max: 29, amount: 0, count: 0 },
    { key: "30_plus", label: "≥ 30 ngày", min: 30, max: Infinity, amount: 0, count: 0 },
  ];
  for (const row of unpaid) {
    const days = Number(row.days_outstanding) || 0;
    const amount = Number(row.total_amount) || 0;
    const bucket = buckets.find((b) => days >= b.min && days <= b.max);
    if (!bucket) continue;
    bucket.amount += amount;
    bucket.count += 1;
  }
  return buckets;
}

export function calcCashflowKpis(summary, ledger = [], dayCount = 1) {
  const totalIn = Number(summary?.total_in) || 0;
  const totalOut = Number(summary?.total_out) || 0;
  const net = totalIn - totalOut;
  const burnRatio = totalIn > 0 ? (totalOut / totalIn) * 100 : totalOut > 0 ? 999 : 0;
  const coverage = totalOut > 0 ? totalIn / totalOut : totalIn > 0 ? 99 : 0;
  const days = Math.max(1, dayCount);
  const avgDailyNet = net / days;
  const volatility =
    ledger.length > 0
      ? Math.abs(net) / Math.max(1, totalIn + totalOut)
      : 0;
  return {
    totalIn,
    totalOut,
    net,
    burnRatio,
    coverage,
    avgDailyNet,
    volatility,
  };
}

export function calcCollectionsKpis(summary, unpaid = [], receipts = []) {
  const receiptTotal = Number(summary?.receipt_total) || 0;
  const unpaidTotal = Number(summary?.unpaid_total) || 0;
  const base = receiptTotal + unpaidTotal;
  const collectionRate = base > 0 ? (receiptTotal / base) * 100 : 0;
  const weightedDays =
    unpaid.length > 0
      ? unpaid.reduce(
          (s, r) => s + Number(r.days_outstanding || 0) * Number(r.total_amount || 0),
          0
        ) / Math.max(1, unpaidTotal)
      : 0;
  const overdue30 = unpaid
    .filter((r) => Number(r.days_outstanding) >= 30)
    .reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const overdueShare = unpaidTotal > 0 ? (overdue30 / unpaidTotal) * 100 : 0;
  const avgReceipt =
    receipts.length > 0 ? receiptTotal / receipts.length : 0;
  return {
    receiptTotal,
    unpaidTotal,
    collectionRate,
    dsoProxy: weightedDays,
    overdue30,
    overdueShare,
    avgReceipt,
  };
}

export function dayCountInclusive(fromDate, toDate) {
  const a = new Date(`${fromDate}T00:00:00`);
  const b = new Date(`${toDate}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 1;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}
